import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Tags, X, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import UnenrollDevice from "./UnenrollDevice";
import { useNotifications } from "@/contexts/NotificationContext";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import * as devicesApi from "@/services/devicesApi";
import { ActionsDropdown, ActionGroup } from "@/components/ui/actions-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

const DeviceActions = () => {
  const { id: deviceId } = useParams();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [isTagRemovalOpen, setIsTagRemovalOpen] = useState(false);
  const [showUnenroll, setShowUnenroll] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"lock" | "unlock" | "checkin" | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const handleConfirmAction = async () => {
    if (!confirmAction || !deviceId) return;
    setIsActionLoading(true);
    try {
    if (confirmAction === "lock") {
        await devicesApi.bulkLock({ deviceIds: [deviceId] });
        toast.success('Lock command sent to device');
      } else if (confirmAction === "unlock") {
        await devicesApi.bulkUnlock({ deviceIds: [deviceId] });
        toast.success('Unlock command sent to device');
      } else if (confirmAction === "checkin") {
        await devicesApi.checkInDevice({ uuid: deviceId });
        toast.success('Device check-in triggered successfully');
      }
    } catch (error) {
      console.error(`Error ${confirmAction}ing device:`, error);
      toast.error(`Failed to ${confirmAction} device`);
    } finally {
      setIsActionLoading(false);
      setConfirmAction(null);
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    }
  };

  const confirmDialogConfig = confirmAction ? {
    lock: { title: "Lock Device", description: "Are you sure you want to lock this device? The device will be remotely locked.", actionLabel: "Lock", loadingLabel: "Locking…" },
    unlock: { title: "Unlock Device", description: "Are you sure you want to unlock this device?", actionLabel: "Unlock", loadingLabel: "Unlocking…" },
    checkin: { title: "Check In Device", description: "Are you sure you want to trigger a check-in for this device?", actionLabel: "Check In", loadingLabel: "Checking In…" },
  }[confirmAction] : null;

  return (
    <>
      <ActionsDropdown
        actions={[
          {
            label: "Management",
            items: [
              { label: "Check In", icon: RefreshCw, onClick: () => setConfirmAction("checkin") },
              { label: "Assign Tags", icon: Tags, onClick: () => setIsTagAssignmentOpen(true) },
              { label: "Remove Tags", icon: X, onClick: () => setIsTagRemovalOpen(true) },
            ],
          } as ActionGroup,
          {
            label: "System",
            items: [
              { label: "Lock Device", icon: Lock, onClick: () => setConfirmAction("lock") },
              { label: "Unlock Device", icon: Unlock, onClick: () => setConfirmAction("unlock") },
              ...(deviceId ? [{ label: "Unenroll Device", icon: undefined, onClick: () => setShowUnenroll(true), destructive: true as const }] : []),
            ],
          } as ActionGroup,
        ]}
      />

      {deviceId && showUnenroll && (
        <UnenrollDevice deviceId={deviceId} deviceName={deviceId} />
      )}

      {deviceId && (
        <>
          <TagAssignmentDialog
            open={isTagAssignmentOpen}
            onOpenChange={setIsTagAssignmentOpen}
            resourceId={deviceId}
            resourceType="device"
            resourceName={deviceId}
            onAssignmentComplete={() => setIsTagAssignmentOpen(false)}
          />
          <TagRemovalDialog
            open={isTagRemovalOpen}
            onOpenChange={setIsTagRemovalOpen}
            deviceId={deviceId}
            deviceName={deviceId}
            onRemovalComplete={() => setIsTagRemovalOpen(false)}
          />
        </>
      )}

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open && !isActionLoading) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialogConfig?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialogConfig?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isActionLoading}
              onClick={async (e) => { e.preventDefault(); await handleConfirmAction(); }}
            >
              {isActionLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{confirmDialogConfig?.loadingLabel}</>
              ) : confirmDialogConfig?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DeviceActions;
